import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Building2, User, AlertCircle, Shield, Info } from 'lucide-react';

export default function PaymentTab({ formData, onInputChange }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-green-100 rounded-lg mr-4">
            <CreditCard className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{t('settings.tabs.payment')}</h3>
            <p className="text-sm text-gray-600">{t('settings.paymentInformationDescription')}</p>
          </div>
        </div>
      </div>

      {/* CLIQ Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t('settings.cliqInformation')}
          </h4>
          <p className="text-sm text-blue-700 mt-1">{t('settings.cliqDescription')}</p>
        </div>
        
        <div className="p-6">
          <div className="space-y-6">
            {/* CLIQ Alias */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                {t('settings.cliqAlias')}
              </label>
              <input
                type="text"
                value={formData.cliqAlias || ''}
                onChange={(e) => onInputChange('cliqAlias', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t('settings.cliqAliasPlaceholder')}
              />
              <p className="mt-2 text-xs text-gray-500 flex items-start gap-2">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {t('settings.cliqAliasHelp')}
              </p>
            </div>

            {/* CLIQ Displayed Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Shield className="w-4 h-4 inline mr-1" />
                {t('settings.cliqDisplayedName')}
              </label>
              <input
                type="text"
                value={formData.cliqAliasDisplayedName || ''}
                onChange={(e) => onInputChange('cliqAliasDisplayedName', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t('settings.cliqDisplayedNamePlaceholder')}
              />
              <p className="mt-2 text-xs text-gray-500 flex items-start gap-2">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {t('settings.cliqDisplayedNameHelp')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* IBAN Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-200">
          <h4 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t('settings.bankInformation')}
          </h4>
          <p className="text-sm text-emerald-700 mt-1">{t('settings.bankDescription')}</p>
        </div>
        
        <div className="p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-1" />
              {t('settings.iban')}
            </label>
            <input
              type="text"
              value={formData.iban || ''}
              onChange={(e) => onInputChange('iban', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors font-mono text-lg"
              placeholder={t('settings.ibanPlaceholder')}
            />
            <p className="mt-2 text-xs text-gray-500 flex items-start gap-2">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              {t('settings.ibanHelp')}
            </p>
          </div>
        </div>
      </div>

      {/* Information Note */}
      <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden">
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-200">
          <h4 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {t('settings.paymentInfoNote')}
          </h4>
        </div>
        
        <div className="p-6">
          <div className="text-sm text-gray-600 space-y-2">
            <p>{t('settings.paymentInfoNoteDescription')}</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-gray-500 ml-4">
              <li>{t('settings.paymentSecurityNote')}</li>
              <li>{t('settings.paymentVerificationNote')}</li>
              <li>{t('settings.paymentUpdateNote')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
